import {createClockIntegrityMonitor} from '../core/time.js';
import {formatElapsedDuration,liveTimerSnapshot} from '../live/stage-timer.js';
import {quickTemperatureErrorText,quickTemperatureInputFromForm} from '../live/quick-temperature-input.js';
import {smokingItemInputFromForm} from './item-editor-input.js';
import {sessionWoodInputFromForm} from './wood-mix-input.js';
import {smokingStageInputFromForm} from './stage-editor-input.js';
import {
  customProductInputFromForm,
  itemPreparationInputFromForm,
  journalItemNoteTextFromForm,
  journalNoteTextFromForm,
  journalPhotoInputFromFile,
  journalSessionTitleFromForm,
  liveNoteInputFromForm
} from './page-input.js';

function requiredFunction(value,label){
  if(typeof value!=='function')throw new TypeError(label+' is required');
  return value;
}

function safeCapture(diagnostics,scope,error,meta){
  try{diagnostics?.capture?.(scope,error,meta);}catch{}
}

export function createJournalPageBindings({
  page,
  documentTarget=globalThis.document,
  runtimeTarget=globalThis,
  FormDataCtor=globalThis.FormData,
  DateCtor=globalThis.Date,
  IntlApi=globalThis.Intl,
  diagnostics,
  getModel=()=>page?.getState?.()?.model||null,
  renderAfter,
  render,
  onRetry=()=>{},
  onCreatePrivateRecipe=async()=>{},
  createClockMonitor=createClockIntegrityMonitor
}={}){
  if(!page||typeof page!=='object')throw new TypeError('journal page controller is required');
  if(!documentTarget?.getElementById||!documentTarget?.querySelectorAll){
    throw new TypeError('journal document target is required');
  }
  requiredFunction(FormDataCtor,'FormData constructor');
  requiredFunction(renderAfter,'journal renderAfter');
  requiredFunction(render,'journal render');
  requiredFunction(getModel,'journal getModel');
  requiredFunction(onRetry,'journal onRetry');
  requiredFunction(onCreatePrivateRecipe,'journal onCreatePrivateRecipe');
  requiredFunction(createClockMonitor,'journal clock monitor factory');

  const listenerRegistry=new WeakMap();
  let liveTicker=null;
  let liveClockMonitor=null;
  let liveClockSessionId=null;
  let liveClockJumpActive=false;

  const byId=id=>documentTarget.getElementById(id);
  const all=selector=>[...(documentTarget.querySelectorAll(selector)||[])];

  function listen(element,event,key,handler){
    if(!element?.addEventListener)return;
    let keys=listenerRegistry.get(element);
    if(!keys){
      keys=new Set();
      listenerRegistry.set(element,keys);
    }
    const token=event+':'+key;
    if(keys.has(token))return;
    keys.add(token);
    element.addEventListener(event,handler);
  }

  function stopLiveTicker(){
    if(liveTicker!=null){
      runtimeTarget.clearInterval?.(liveTicker);
      liveTicker=null;
    }
  }

  function resetLiveClockMonitor(){
    liveClockMonitor?.reset?.();
    liveClockMonitor=null;
    liveClockSessionId=null;
    liveClockJumpActive=false;
  }

  function liveRemainingText(snapshot){
    const stage=snapshot?.stage;
    if(!stage||stage.plannedMinutes==null)return 'Etap bez planowanego czasu';
    if(stage.overdue)return 'Po planie o '+formatElapsedDuration(stage.overdueMs);
    return 'Pozostało '+formatElapsedDuration(stage.remainingMs)+' z '+Math.round(stage.plannedMinutes)+' min';
  }

  function wireLiveTicker(){
    const journal=getModel();
    const model=journal?.liveDashboard;
    if(!model||!model.source?.actualStartAt||!model.source?.activeStageStartedAt)return;
    const sessionId=String(journal?.session?.id||'').trim();
    if(!liveClockMonitor||liveClockSessionId!==sessionId){
      resetLiveClockMonitor();
      liveClockMonitor=createClockMonitor();
      liveClockSessionId=sessionId;
    }

    const session={
      status:'smoking',
      actualStartAt:model.source.actualStartAt,
      activeStageId:model.source.activeStageId,
      activeStageStartedAt:model.source.activeStageStartedAt
    };
    const stageTarget=model.currentStage?.target||null;

    const tick=()=>{
      try{
        const clock=liveClockMonitor.observe();
        const snapshot=liveTimerSnapshot({session,stageTarget,now:clock.effectiveNowMillis});
        const sessionNode=documentTarget.querySelector?.('[data-live-session-elapsed]');
        const stageNode=documentTarget.querySelector?.('[data-live-stage-elapsed]');
        const remainingNode=documentTarget.querySelector?.('[data-live-stage-remaining]');
        const warningNode=documentTarget.querySelector?.('[data-live-clock-warning]');
        if(sessionNode)sessionNode.textContent=formatElapsedDuration(snapshot.session.elapsedMs);
        if(stageNode)stageNode.textContent=formatElapsedDuration(snapshot.stage.elapsedMs);
        if(remainingNode)remainingNode.textContent=liveRemainingText(snapshot);
        if(warningNode)warningNode.hidden=clock.status!=='clock_jump';

        if(clock.status==='clock_jump'){
          if(!liveClockJumpActive){
            const error=Object.assign(new Error('Device clock changed during Live session'),{code:'LIVE_CLOCK_JUMP'});
            safeCapture(diagnostics,'journal.live-clock',error,{
              direction:clock.direction,
              drift_ms:Math.round(clock.driftMs)
            });
          }
          liveClockJumpActive=true;
        }else{
          liveClockJumpActive=false;
        }
      }catch(error){
        safeCapture(diagnostics,'journal.live-timer',error);
        stopLiveTicker();
      }
    };

    tick();
    liveTicker=runtimeTarget.setInterval?.(tick,1000)??null;
  }

  function bind(){
    stopLiveTicker();
    wireLiveTicker();

    listen(byId('journal-retry'),'click','retry',()=>{
      onRetry();
      renderAfter(page.refresh(),{sync:false});
    });

    for(const element of all('[data-journal-session-conflict-refresh]')){
      listen(element,'click','session-conflict-refresh',()=>{
        const sessionId=element.getAttribute('data-journal-session-conflict-refresh');
        const confirmed=runtimeTarget.confirm?.(
          'Przyjąć aktualny stan sesji z serwera? Bieżący etap i jego czas zostaną zastąpione stanem serwera. Niezależne lokalne temperatury, notatki i zdjęcia pozostaną zachowane.'
        );
        if(confirmed===false)return;
        renderAfter(page.acceptRemoteLiveSessionState(sessionId),{sync:false});
      });
    }

    for(const element of all('[data-journal-stage-conflicts-discard]')){
      listen(element,'click','stage-conflict-discard',()=>{
        const sessionId=element.getAttribute('data-journal-stage-conflicts-discard');
        const confirmed=runtimeTarget.confirm?.(
          'Odrzucić przegraną lokalną zmianę etapu? Usunięte zostaną tylko odrzucone wpisy zmiany etapu. Pozostałe lokalne dane Live zostaną zachowane.'
        );
        if(confirmed===false)return;
        renderAfter(page.discardRejectedLiveStageEvidence(sessionId),{sync:false});
      });
    }

    const createForm=byId('journal-create-form');
    listen(createForm,'submit','create',event=>{
      event.preventDefault();
      renderAfter(page.createDraft({title:journalSessionTitleFromForm(new FormDataCtor(createForm))}));
    });

    for(const element of all('[data-journal-session-open]')){
      listen(element,'click','session-open',()=>{
        renderAfter(page.open(element.getAttribute('data-journal-session-open')),{sync:false});
      });
    }

    listen(byId('journal-back'),'click','back',()=>{
      renderAfter(page.refresh(),{sync:false});
    });

    listen(byId('live-quick-temperature'),'click','quick-temperature',()=>{
      const panel=byId('live-temperature-panel');
      if(!panel)return;
      panel.hidden=!panel.hidden;
      if(!panel.hidden){
        panel.scrollIntoView?.({behavior:'smooth',block:'nearest'});
        panel.querySelector?.('input[name="temperatureValue"]')?.focus?.();
      }
    });

    const liveTemperatureForm=byId('live-temperature-form');
    const liveTemperatureError=byId('live-temperature-error');
    const liveTemperatureValue=liveTemperatureForm?.querySelector?.('input[name="temperatureValue"]');
    listen(liveTemperatureValue,'input','temperature-clear-error',()=>{
      if(liveTemperatureError)liveTemperatureError.textContent='';
    });
    listen(liveTemperatureForm,'submit','temperature-submit',event=>{
      event.preventDefault();
      try{
        const input=quickTemperatureInputFromForm(new FormDataCtor(liveTemperatureForm));
        if(liveTemperatureError)liveTemperatureError.textContent='';
        renderAfter(page.logTemperature(input));
      }catch(error){
        if(liveTemperatureError)liveTemperatureError.textContent=quickTemperatureErrorText(error);
        liveTemperatureValue?.focus?.();
      }
    });

    for(const element of all('[data-live-stage-next]')){
      listen(element,'click','live-stage-next',()=>{
        renderAfter(page.transitionLiveStage(element.getAttribute('data-live-stage-next')));
      });
    }

    for(const element of all('[data-live-stage-skip]')){
      listen(element,'click','live-stage-skip',async()=>{
        const id=element.getAttribute('data-live-stage-skip');
        const plan=await page.requestLiveStageTransition(id);
        const count=Number(plan?.skippedStageIds?.length||0);
        if(runtimeTarget.confirm?.('Pominąć '+count+' etap(y) i przejść dalej? Ta decyzja zostanie zachowana w historii.')===false)return;
        renderAfter(page.transitionLiveStage(id,{confirmed:true}));
      });
    }

    for(const element of all('[data-live-stage-reopen]')){
      listen(element,'click','live-stage-reopen',async()=>{
        const id=element.getAttribute('data-live-stage-reopen');
        await page.requestLiveStageTransition(id);
        if(runtimeTarget.confirm?.('Ponownie otworzyć wcześniejszy etap? Poprzedni przebieg pozostanie w historii.')===false)return;
        renderAfter(page.transitionLiveStage(id,{confirmed:true}));
      });
    }

    const liveCustomStageForm=byId('live-custom-stage-form');
    listen(liveCustomStageForm,'submit','custom-stage',event=>{
      event.preventDefault();
      const name=String(new FormDataCtor(liveCustomStageForm).get('customStageName')||'').trim();
      if(!name)return;
      if(runtimeTarget.confirm?.('Dodać własny etap „'+name+'” i rozpocząć go teraz?')===false)return;
      renderAfter(page.insertLiveCustomStage(name));
    });

    const liveNotificationForm=byId('live-notification-form');
    listen(liveNotificationForm,'submit','live-notifications',event=>{
      event.preventDefault();
      const data=new FormDataCtor(liveNotificationForm);
      const intervalRaw=String(data.get('temperatureIntervalMinutes')||'').trim();
      renderAfter(page.configureLiveNotifications({
        enabled:data.get('liveNotificationsEnabled')==='on',
        stageEndEnabled:data.get('stageEndEnabled')==='on',
        stageEndLeadMinutes:Number(data.get('stageEndLeadMinutes')||5),
        temperatureIntervalMinutes:intervalRaw?Number(intervalRaw):null
      }),{sync:false});
    });

    function toggleLivePanel(id,inputSelector=null){
      const panel=byId(id);
      if(!panel)return;
      panel.hidden=!panel.hidden;
      if(!panel.hidden){
        panel.scrollIntoView?.({behavior:'smooth',block:'nearest'});
        if(inputSelector)panel.querySelector?.(inputSelector)?.focus?.();
      }
    }

    listen(byId('live-quick-note'),'click','quick-note',()=>{
      toggleLivePanel('live-note-panel','textarea[name="liveNote"]');
    });

    const liveNoteForm=byId('live-note-form');
    const liveNoteError=byId('live-note-error');
    listen(liveNoteForm,'submit','live-note',event=>{
      event.preventDefault();
      try{
        const input=liveNoteInputFromForm(new FormDataCtor(liveNoteForm));
        if(liveNoteError)liveNoteError.textContent='';
        renderAfter(page.captureLiveNote(input));
      }catch(error){
        if(liveNoteError)liveNoteError.textContent=String(error?.message||'Nie udało się zapisać notatki.');
        liveNoteForm.querySelector?.('textarea[name="liveNote"]')?.focus?.();
      }
    });

    listen(byId('live-quick-photo'),'click','quick-photo',()=>{
      toggleLivePanel('live-photo-panel','input[name="livePhoto"]');
    });

    const livePhotoForm=byId('live-photo-form');
    const livePhotoError=byId('live-photo-error');
    listen(livePhotoForm,'submit','live-photo',event=>{
      event.preventDefault();
      try{
        const data=new FormDataCtor(livePhotoForm);
        const file=livePhotoForm.querySelector?.('input[name="livePhoto"]')?.files?.[0];
        const itemId=String(data.get('livePhotoItemId')||'').trim()||null;
        const session=getModel()?.session;
        const input=journalPhotoInputFromFile(file,{
          sessionId:session?.id,
          itemId,
          stageId:session?.activeStageId||null,
          capturedAt:new DateCtor().toISOString()
        });
        if(livePhotoError)livePhotoError.textContent='';
        renderAfter(page.captureLivePhoto(input,{file}),{sync:false});
      }catch(error){
        if(livePhotoError)livePhotoError.textContent=String(error?.message||'Nie udało się zapisać zdjęcia.');
      }
    });

    listen(byId('live-timeline-more'),'click','timeline-more',()=>{
      renderAfter(page.showEarlierLiveTimeline(),{sync:false});
    });

    listen(byId('live-quick-event'),'click','quick-event',()=>{
      const panel=documentTarget.querySelector?.('.live-event-panel');
      if(panel){
        panel.open=true;
        panel.scrollIntoView?.({behavior:'smooth',block:'nearest'});
      }
    });
    for(const element of all('[data-live-event]')){
      listen(element,'click','live-event',()=>{
        const eventType=element.getAttribute('data-live-event');
        const input={
          woodId:element.getAttribute('data-live-wood-id')||null,
          itemId:element.getAttribute('data-live-item-id')||null
        };
        if(eventType==='custom'){
          const customText=runtimeTarget.prompt?.('Co wydarzyło się podczas wędzenia?')?.trim();
          if(!customText)return;
          input.customText=customText;
        }
        renderAfter(page.logLiveEvent(eventType,input));
      });
    }

    const titleForm=byId('journal-title-form');
    listen(titleForm,'submit','title',event=>{
      event.preventDefault();
      renderAfter(page.saveTitle(journalSessionTitleFromForm(new FormDataCtor(titleForm))));
    });

    const scheduleForm=byId('journal-schedule-form');
    listen(scheduleForm,'submit','schedule',event=>{
      event.preventDefault();
      renderAfter(page.saveSchedule(new FormDataCtor(scheduleForm),{
        timeZone:IntlApi.DateTimeFormat().resolvedOptions().timeZone,
        utcOffsetMinutes:-new DateCtor().getTimezoneOffset()
      }));
    });

    const equipmentForm=byId('journal-equipment-form');
    listen(equipmentForm,'submit','equipment',event=>{
      event.preventDefault();
      renderAfter(page.setEquipment(new FormDataCtor(equipmentForm).get('smokerId')));
    });

    const customForm=byId('journal-custom-product-form');
    listen(customForm,'submit','custom-product',event=>{
      event.preventDefault();
      renderAfter(page.createCustomProduct(customProductInputFromForm(new FormDataCtor(customForm))));
    });

    const itemForm=byId('smoking-item-form');
    listen(itemForm,'submit','item-save',event=>{
      event.preventDefault();
      const model=getModel();
      renderAfter(page.saveItem(smokingItemInputFromForm(new FormDataCtor(itemForm),{
        catalogue:model?.refs?.productCatalogue||[],
        customProducts:model?.refs?.customProducts||[]
      })));
    });
    listen(byId('cancel-smoking-item-edit'),'click','item-cancel',()=>{
      renderAfter(page.cancelItemEdit(),{sync:false});
    });
    for(const element of all('[data-journal-item-edit]')){
      listen(element,'click','item-edit',()=>renderAfter(
        page.beginItemEdit(element.getAttribute('data-journal-item-edit')),
        {sync:false}
      ));
    }
    for(const [selector,attribute,direction] of [
      ['[data-journal-item-up]','data-journal-item-up','up'],
      ['[data-journal-item-down]','data-journal-item-down','down']
    ]){
      for(const element of all(selector)){
        listen(element,'click','item-'+direction,()=>renderAfter(page.moveItem(
          element.getAttribute(attribute),
          direction
        )));
      }
    }
    for(const element of all('[data-journal-item-remove]')){
      listen(element,'click','item-remove',()=>{
        if(runtimeTarget.confirm?.('Usunąć produkt z planu?')===false)return;
        renderAfter(page.removeItem(element.getAttribute('data-journal-item-remove')));
      });
    }

    for(const form of all('[data-journal-preparation-form]')){
      listen(form,'submit','preparation',event=>{
        event.preventDefault();
        const formData=new FormDataCtor(form);
        renderAfter(page.savePreparation(
          form.getAttribute('data-journal-preparation-form'),
          itemPreparationInputFromForm(formData),
          {itemNote:journalItemNoteTextFromForm(formData)}
        ));
      });
    }

    const woodForm=byId('session-wood-form');
    listen(woodForm,'submit','wood-save',event=>{
      event.preventDefault();
      const model=getModel();
      renderAfter(page.saveWood(sessionWoodInputFromForm(new FormDataCtor(woodForm),{
        catalogue:model?.refs?.woodCatalogue||[],
        inventory:model?.refs?.woodInventory||[]
      })));
    });
    listen(byId('cancel-session-wood-edit'),'click','wood-cancel',()=>{
      renderAfter(page.cancelWoodEdit(),{sync:false});
    });
    for(const element of all('[data-journal-wood-edit]')){
      listen(element,'click','wood-edit',()=>renderAfter(
        page.beginWoodEdit(element.getAttribute('data-journal-wood-edit')),
        {sync:false}
      ));
    }
    for(const [selector,attribute,direction] of [
      ['[data-journal-wood-up]','data-journal-wood-up','up'],
      ['[data-journal-wood-down]','data-journal-wood-down','down']
    ]){
      for(const element of all(selector)){
        listen(element,'click','wood-'+direction,()=>renderAfter(page.moveWood(
          element.getAttribute(attribute),
          direction
        )));
      }
    }
    for(const element of all('[data-journal-wood-remove]')){
      listen(element,'click','wood-remove',()=>{
        if(runtimeTarget.confirm?.('Usunąć drewno z planu?')===false)return;
        renderAfter(page.removeWood(element.getAttribute('data-journal-wood-remove')));
      });
    }

    const stageForm=byId('smoking-stage-form');
    listen(stageForm,'submit','stage-save',event=>{
      event.preventDefault();
      const model=getModel();
      renderAfter(page.saveStage(smokingStageInputFromForm(
        new FormDataCtor(stageForm),
        {items:model?.itemState?.items||[]}
      )));
    });
    listen(byId('cancel-smoking-stage-edit'),'click','stage-cancel',()=>{
      renderAfter(page.cancelStageEdit(),{sync:false});
    });
    for(const element of all('[data-journal-stage-edit]')){
      listen(element,'click','stage-edit',()=>renderAfter(
        page.beginStageEdit(element.getAttribute('data-journal-stage-edit')),
        {sync:false}
      ));
    }
    for(const [selector,attribute,direction] of [
      ['[data-journal-stage-up]','data-journal-stage-up','up'],
      ['[data-journal-stage-down]','data-journal-stage-down','down']
    ]){
      for(const element of all(selector)){
        listen(element,'click','stage-'+direction,()=>renderAfter(page.moveStage(
          element.getAttribute(attribute),
          direction
        )));
      }
    }
    for(const element of all('[data-journal-stage-remove]')){
      listen(element,'click','stage-remove',async()=>{
        const id=element.getAttribute('data-journal-stage-remove');
        const plan=await page.removeStage(id);
        const detail=plan?.hasTargetData?' Etap ma zapisane cele temperatury/czasu.':'';
        if(runtimeTarget.confirm?.('Usunąć etap z planu?'+detail)===false)return;
        renderAfter(page.removeStage(id,{confirmed:true}));
      });
    }

    const noteForm=byId('journal-note-form');
    listen(noteForm,'submit','session-note',event=>{
      event.preventDefault();
      renderAfter(page.saveSessionNote(journalNoteTextFromForm(new FormDataCtor(noteForm))));
    });
    listen(byId('journal-note-clear'),'click','session-note-clear',()=>{
      renderAfter(page.clearSessionNote());
    });

    const photoForm=byId('journal-photo-form');
    listen(photoForm,'submit','photo-attach',event=>{
      event.preventDefault();
      const file=photoForm.querySelector?.('input[name="photo"]')?.files?.[0];
      const sessionId=getModel()?.session?.id;
      renderAfter(page.attachPhoto(
        journalPhotoInputFromFile(file,{sessionId}),
        {file}
      ),{sync:false});
    });
    for(const element of all('[data-journal-photo-remove]')){
      listen(element,'click','photo-remove',()=>{
        if(runtimeTarget.confirm?.('Usunąć zdjęcie z planu?')===false)return;
        renderAfter(page.removePhoto(element.getAttribute('data-journal-photo-remove')),{sync:false});
      });
    }

    listen(byId('journal-save-planned'),'click','save-planned',()=>{
      renderAfter(page.saveAsPlanned());
    });

    listen(byId('journal-save-private-recipe'),'click','save-private-recipe',()=>{
      void Promise.resolve(onCreatePrivateRecipe()).catch(error=>{
        safeCapture(diagnostics,'journal.private-recipe',error);
      });
    });

    listen(byId('journal-start-smoking'),'click','start-smoking',async()=>{
      const plan=await page.requestStartSmoking();
      if(plan?.status!=='confirmation_required'){
        render();
        return;
      }
      const warnings=(plan.warnings||[]).map(item=>'• '+item.message).join('\n');
      const prompt='Rozpocząć wędzenie od etapu „'+String(plan.stageName||'pierwszy etap')+'”?'
        +(warnings?'\n\nPlan ma ostrzeżenia:\n'+warnings:'');
      if(runtimeTarget.confirm?.(prompt)===false)return;
      renderAfter(page.startSmoking({stageId:plan.stageId,confirmed:true}));
    });

    listen(byId('live-finish-session'),'click','finish-session',async()=>{
      const plan=await page.finishSession();
      if(plan?.status!=='confirmation_required'){
        render();
        return;
      }
      const stageWarning=plan.activeStageId?' Aktywny etap zostanie zamknięty z bieżącym czasem.':'';
      if(runtimeTarget.confirm?.('Zakończyć to wędzenie?'+stageWarning+' Historia Live pozostanie zachowana.')===false)return;
      renderAfter(page.finishSession({confirmed:true}),{sync:false});
    });

    const outcomeForm=byId('journal-outcome-form');
    listen(outcomeForm,'submit','outcome',event=>{
      event.preventDefault();
      const data=new FormDataCtor(outcomeForm);
      const optionalRating=name=>data.get(name)===''?null:Number(data.get(name));
      renderAfter(page.saveOutcome({
        overallRating:Number(data.get('overallRating')),
        taste:optionalRating('taste'),
        smoke:optionalRating('smoke'),
        juiciness:optionalRating('juiciness'),
        texture:optionalRating('texture'),
        appearance:optionalRating('appearance'),
        lessonsLearned:String(data.get('lessonsLearned')||'').trim()||null
      }),{sync:false});
    });

    listen(byId('journal-cancel-session'),'click','cancel-session',()=>{
      if(runtimeTarget.confirm?.('Anulować to wędzenie?')===false)return;
      renderAfter(page.cancelSession({confirmed:true}));
    });
    listen(byId('journal-delete-session'),'click','delete-session',()=>{
      if(runtimeTarget.confirm?.('Usunąć ten plan? Tej operacji nie cofniemy po synchronizacji.')===false)return;
      renderAfter(page.deleteSession({confirmed:true}));
    });

    return Object.freeze({bound:true});
  }

  function dispose(){
    stopLiveTicker();
    resetLiveClockMonitor();
  }

  return Object.freeze({bind,stopLiveTicker,dispose});
}
